import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {NucleotideType} from 'biojs/BioConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import HelpButton from 'help/HelpButton';
import {
  downloadOligos,
  PRIMERS_COPIED_MSG,
  formatOligos,
  OligoToFormat,
  getOligoFilename,
} from 'molbio/oligos/ExportOligoUtil';
import {PrimerRowProps} from 'toolbar/primer-panel/PrimerRow';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {FloatingPlacement, FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import AddToWorklistModal from 'worklists/AddToWorklistModal';
import {getWorklistType} from 'worklists/WorklistUtil';

import PrimerSchemaSelectorModal from './PrimerSchemaSelectorModal';

export type OligoSchemaFields = Array<{
  id: string;
  name: string;
  eligibleNucleotideTypes: Set<NucleotideType>;
}>;
export type AddPrimersToField = (fieldId: string, oligoIds: Array<string>) => void;

export interface SelectedOligoActionsRowProps {
  primers: Array<PrimerRowProps>;
  sequenceName: string;
  oligoSchemaFields: OligoSchemaFields;
  addPrimersToField: AddPrimersToField;
}

interface DropdownAction {
  action: string;
}
export interface ExportPrimersData extends DropdownAction {
  oligos: Array<OligoToFormat>;
  filename: string;
}

export interface PrimerIds extends DropdownAction {
  primerIds: Array<string>;
}

export default function SelectedOligoActionsRow({
  primers,
  sequenceName,
  oligoSchemaFields,
  addPrimersToField,
}: SelectedOligoActionsRowProps): JSX.Element {
  const exportData = primers.map((p) => ({name: p.name, bases: p.bases, nucleotideType: p.nucleotideType}));
  const exportFileName = getOligoFilename(sequenceName);
  const primerIds = primers.map((p) => p.oligoId);
  const filteredOligoSchemaFields = oligoSchemaFields.filter((field) =>
    primers.every((p) => field.eligibleNucleotideTypes.has(p.nucleotideType))
  );
  const popoverContent = (
    <div>
      <p>Use the Pairs tab above or link primers directly on the sequence map:</p>
      <p>1. Click the first primer (either on the sequence map or in this list)</p>
      <p>2. Shift-click the second primer (either on the sequence map or in this list)</p>
      <p>3. Right-click the selected region on the sequence map to bring up the context menu</p>
      <p>4. Select option to 'Link Primers'</p>
      <p>Please note that RNA primers cannot be linked currently.</p>
      <img src="/static/img/primer_pair_selection.png" height={125} />
    </div>
  );

  return (
    <div className="row" aria-label="Selected oligo actions" role="group">
      <div className="col col-md-6">
        <Dropdown
          menu={
            <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE}>
              <Option
                onSelect={() => {
                  downloadOligos({oligos: exportData, filename: exportFileName});
                }}
              >
                Download to CSV
              </Option>
              <Option
                onSelect={() => {
                  copyToClipboard(formatOligos(exportData, 'tsv'), {
                    showToastOnSuccess: true,
                    toastText: PRIMERS_COPIED_MSG,
                  });
                }}
              >
                Copy Primers to Clipboard
              </Option>
              <Option
                isDisabled={
                  !isFeatureEnabled({feature: StandardFeature.EDIT, itemType: ModelTypeConstants.DNA_OLIGO})
                }
                onSelect={() => {
                  legacyRunModal(PrimerSchemaSelectorModal, {
                    oligoIds: primerIds,
                    oligoNucleotideTypes: new Set(primers.map((p) => p.nucleotideType)),
                  });
                }}
              >
                Set Schema
              </Option>
              {BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS && (
                <Option
                  onSelect={() => {
                    legacyRunModal(AddToWorklistModal, {
                      ids: primerIds,
                      worklistType: getWorklistType(primerIds),
                      refreshOptions: {
                        refreshWorklistItemsSearch: true,
                        refreshWorklistsSearch: true,
                        refreshRecentWorklists: true,
                      },
                    });
                  }}
                >
                  Add to Worklist
                </Option>
              )}
            </Menu>
          }
        >
          <Button
            isDropdown={true}
            disabled={primers.length === 0}
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.SMALL}
            text="Bulk Actions"
          />
        </Dropdown>
      </div>
      <div className={classNames('col', 'col-md-6', css(styles.rightDropdownContainer))}>
        <HelpButton
          renderContent={() => popoverContent}
          title="Looking for Link Primers?"
          interaction={FloatingInteraction.HOVER}
          placement={FloatingPlacement.LEFT}
          iconClassName={css(styles.icon)}
        />
        {oligoSchemaFields.length > 0 && (
          <Dropdown
            menu={
              <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE}>
                {filteredOligoSchemaFields.map((field, i) => (
                  <Option
                    key={i}
                    onSelect={() => {
                      addPrimersToField(
                        field.id,
                        primers.map(({oligoId}) => oligoId)
                      );
                    }}
                  >
                    {field.name}
                  </Option>
                ))}
              </Menu>
            }
          >
            <Button
              isDropdown={true}
              disabled={primers.length === 0 || filteredOligoSchemaFields.length === 0}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.SMALL}
              text="Add to field"
            />
          </Dropdown>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  rightDropdownContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minHeight: '28px', // Ensures the question icon is vertically centered even when the dropdown is not present
  },
  icon: {
    marginRight: '6px',
  },
});
